import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import {
	checkSubsidy,
	clearSelectedSubsidies,
	selectSubsidy,
	setCheckSubsidyResult,
	setCheckedTravellers,
	setSubsidiesError,
	setSubsidiesLoading,
	setSubsidiesResultsOpen,
	setSubsidiesState
} from './actions';
import {
	CheckoutOrderFragment,
	SubsidyTravellerParameters,
	OrderSubsidyInfoDocument,
	OrderSubsidyInfoQueryVariables,
	OrderSubsidyInfoQueryResult,
	Subsidy
} from '@websky/graphql';
import { createClient } from '../../../graphql-client';
import { getOrderId } from '../order/selectors';
import { GraphQLError } from 'graphql';
import { getSubsidiesResults } from './selectors';

const checkSubsidyRequest = async (orderId: string, travellers: SubsidyTravellerParameters[]) => {
	const client = createClient();

	return await client.query<OrderSubsidyInfoQueryResult, OrderSubsidyInfoQueryVariables>({
		query: OrderSubsidyInfoDocument,
		fetchPolicy: 'no-cache',
		variables: {
			id: orderId,
			travellers
		}
	});
};

export function* setSubsidies(order: CheckoutOrderFragment) {
	yield put(setSubsidiesState(order));
}

export function* selectSubsidiesByDefault({ payload }: ReturnType<typeof setCheckSubsidyResult>) {
	const subsidiesByTravellers: ReturnType<typeof getSubsidiesResults> = yield select(getSubsidiesResults);

	for (const [passengerId, subsidiesBySegments] of Object.entries(subsidiesByTravellers)) {
		const subsidiesToSelect: Subsidy[] = [];

		for (const [segmentId, subsidies] of Object.entries(subsidiesBySegments)) {
			for (const subsidy of subsidies) {
				if (!subsidy.rejectMessage) {
					subsidiesToSelect.push({
						segmentId,
						subsidyId: subsidy.id
					});
				}
			}
		}

		const travellerHasOnlyOneSubsidy = new Set(subsidiesToSelect.map(subsidy => subsidy.subsidyId)).size === 1;

		if (travellerHasOnlyOneSubsidy) {
			for (const subsidyToSelect of subsidiesToSelect) {
				yield put(
					selectSubsidy({
						subsidyId: subsidyToSelect.subsidyId,
						segmentId: subsidyToSelect.segmentId,
						passengerId: passengerId
					})
				);
			}
		}
	}
}

export function* checkSubsidies(action: ReturnType<typeof checkSubsidy>) {
	try {
		yield put(setSubsidiesLoading(true));
		const orderId: string = yield select(getOrderId);

		yield put(clearSelectedSubsidies());
		yield put(setCheckedTravellers(action.payload));

		const orderSubsidyInfo: OrderSubsidyInfoQueryResult = yield call(checkSubsidyRequest, orderId, action.payload);

		if (
			!orderSubsidyInfo.data.OrderSubsidyInfo.success &&
			orderSubsidyInfo.data.OrderSubsidyInfo.segments.length === 0
		) {
			yield put(setSubsidiesError('NO_SUBSIDIES_FOR_TRAVELLERS'));
			yield put(setSubsidiesLoading(false));
			yield put(setSubsidiesResultsOpen(true));

			return;
		}

		yield put(setCheckSubsidyResult(orderSubsidyInfo.data.OrderSubsidyInfo));

		yield put(setSubsidiesLoading(false));
		yield put(setSubsidiesResultsOpen(true));
	} catch (e) {
		const gqlError = e as GraphQLError;
		yield put(setSubsidiesError(gqlError['debugMessage'] || gqlError['message']));
		yield put(setSubsidiesLoading(false));
	}
}

export function* subsidiesSaga() {
	yield takeEvery(setCheckSubsidyResult.toString(), selectSubsidiesByDefault);
	yield takeLatest(checkSubsidy.toString(), checkSubsidies);
}
